import { ErrorHandler, Injectable, NgModule } from '@angular/core';
import {
  BrowserModule,
  HAMMER_GESTURE_CONFIG,
  HammerGestureConfig,
} from '@angular/platform-browser';
import { NoopAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateModule } from '@ngx-translate/core';
import { AppPreloader } from './app-preloader.service';
import { AppDispatcher, appReducer, AppStore } from './store';
import { AppRoutes } from './app.routes';
import {
  accountDataEffects,
  AccountDataModule,
} from './modules/account-data/account-data.module';
import {
  accountUserDataEffects,
  AccountUserDataModule,
} from './modules/account-user-data/account-user-data.module';
import {
  authDataEffects,
  AuthDataModule,
} from './modules/auth-data/auth-data.module';
import { AuthModule } from './modules/auth/auth.module';
import {
  browserManagerDataEffects,
  BrowserManagerDataModule,
} from './modules/browser-manager-data/browser-manager-data.module';
import { DialogModule } from './modules/dialog/dialog.module';
import { AppErrorHandler } from './modules/error-reporting/error-handler';
import { ErrorReportingModule } from './modules/error-reporting/error-reporting.module';
import {
  featuresDataEffects,
  FeaturesDataModule,
} from './modules/features-data/features-data.module';
import { FilesModule } from './modules/files/files.module';
import { HttpModule } from './modules/http/http.module';
import { LocalizationModule } from './modules/localization/localization.module';
import { MainModule } from './modules/main';
import { NavigationModule } from './modules/navigation/navigation.module';
import {
  settingsDataEffects,
  SettingsDataModule,
} from './modules/settings-data/settings-data.module';
import { SharedModule } from './modules/shared/shared.module';
import { TrackingModule } from './modules/tracking/tracking.module';
import {
  xeroDataEffects,
  XeroDataModule,
} from './modules/xero-data/xero-data.module';
import { TRANSLATE_CONFIG } from './app-translate.loader';
import * as fromContainers from './containers';
import * as fromComponents from './components';
import * as fromGuards from './guards';
import { I2gNgPerformanceModule } from 'i2g-ng-performance';
import { ToasterModule } from './modules/toaster/toaster.module';
import { I2gNgUtilsModule } from 'i2g-ng-utils';
import { I2gNgUiModule } from 'i2g-ng-ui';
import { I2gNgTrackingModule } from 'i2g-ng-tracking';
import { verifyAccountEffects } from './modules/verify-account/verify-account.module';
import { JobDataModule } from './modules/job-data/job-data.module';
import { environment } from '../environments/environment';
import { EnvironmentsEnum } from '../environments/environments.enum';
import { ModalModule } from 'i2g-ngx-modialog';
import { InstantWebsiteDataModule } from './modules/instant-website-data/instant-website-data.module';

@Injectable()
export class HammerConfig extends HammerGestureConfig {
  overrides = <any>{
    pinch: { enable: false },
    rotate: { enable: false },
  };
}

@NgModule({
  imports: [
    BrowserModule,
    NoopAnimationsModule,
    HttpModule.forRoot(),
    StoreModule.forRoot(appReducer, {
      runtimeChecks: {
        // see: projects/invoice2go/src/app/modules/main/containers/main/main.component.ts:246
        // also: https://github.com/immutable-js-oss/immutable-js/issues/140
        // and: https://github.com/immutable-js/immutable-js/issues/1228
        // and: https://github.com/immutable-js/immutable-js/issues/1717
        // TODO: can we not rely on hashCode in main.component.ts:246?
        strictStateImmutability: false, // breaks calling .hashCode() on our models
        strictActionImmutability: false, // breaks calling .hashCode() on our models
        strictActionWithinNgZone: true,
        strictActionTypeUniqueness: true,
      },
    }),
    // Instrumentation must be imported after importing StoreModule (config is optional)
    environment.env !== EnvironmentsEnum.production
      ? StoreDevtoolsModule.instrument()
      : [],
    EffectsModule.forRoot([
      ...verifyAccountEffects,
      ...featuresDataEffects,
      ...accountDataEffects,
      ...accountUserDataEffects,
      ...browserManagerDataEffects,
      ...authDataEffects,
      ...settingsDataEffects,
      ...xeroDataEffects,
    ]),
    ModalModule.forRoot(),
    DialogModule.forRoot(),
    SharedModule.forRoot(),
    NavigationModule.forRoot(),
    BrowserManagerDataModule.forRoot(),
    AuthDataModule.forRoot(),
    TrackingModule.forRoot(),
    TranslateModule.forRoot(TRANSLATE_CONFIG),
    LocalizationModule.forRoot(),
    ErrorReportingModule.forRoot(),
    FeaturesDataModule.forRoot(),
    AccountDataModule.forRoot(),
    AccountUserDataModule.forRoot(),
    SettingsDataModule.forRoot(),
    XeroDataModule.forRoot(),
    JobDataModule.forRoot(),
    InstantWebsiteDataModule.forRoot(),
    FilesModule.forRoot(),
    I2gNgPerformanceModule.forRoot(),
    I2gNgTrackingModule.forRoot(),
    AuthModule,
    ToasterModule,
    MainModule,
    I2gNgUtilsModule,
    I2gNgUiModule,
    AppRoutes,
  ],
  declarations: [...fromContainers.containers, ...fromComponents.components],
  providers: [
    {
      provide: ErrorHandler,
      useClass: AppErrorHandler,
    },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: HammerConfig,
    },
    ...fromGuards.guards,
    AppPreloader,
    AppDispatcher,
    AppStore,
  ],
  bootstrap: [fromContainers.AppComponent],
})
export class AppModule {}
